import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { RootState } from '@/store';
import { billingApiSlice } from '@/store/slices/api/billingApiSlice.ts';
import { patientsApiSlice } from '@/store/slices/api/patientsApiSlice.ts';
import { UNUSED_DATA_RETENTION_SECONDS } from '@/utils/constants';
import { API_ENDPOINTS, API_SLICE, BASE_URL } from '@/utils/constants/apiEndpoints.ts';

import { getFilters } from '@/pages/physician/PatientListPage/util';
import {
  AddImageToAlertRequest,
  Alert,
  AlertResponse,
  CreateAlertRequest,
  DeleteAlertRequest,
  RemoveImageFromAlertRequest,
  ReportImage,
  UpdateAlertRequest,
  UpdateMultipleNotesRequest,
  UpdateMultipleNotesResponse
} from '@/types/alert';
import { CountersResponse, CreateCountersRequest, UpdateCountersRequest } from '@/types/counters';
import { AddAttachmentsRequest, GetReportAttachmentsResponse } from '@/types/reportAttachment';
import {
  ApplyReportTemplateRequest,
  FetchOrgPhysicianTemplatesResponse,
  FetchReportTemplatesRequest,
  FetchReportTemplatesResponse,
  ReplaceReportTemplateRequest,
  ReplaceReportTemplateResponse,
  ReportTemplateResponse
} from '@/types/reportTemplate';
import {
  GetPatientReportsResponse,
  NavigationReportIdsRequestDto,
  ProgrammedParameters,
  ReportColor,
  ReportInClinicEpisode,
  SearchUnsignedReportsRequest,
  SearchUnsignedReportsResponse,
  SignReportRequest,
  SignReportResponse,
  TransmissionCategoryResponse,
  TransmissionReportDetailsResponse,
  TransmissionReportListPaginatedRequestDto,
  UnsignedReportCountsByColorRequest,
  UnsignedReportCountsByColorResponse,
  UpdateProgrammedParametersRequest,
  UpdateTransmissionReportParams
} from '@/types/transmissionReport';
import {
  UpdateMultipleOverridesRequest,
  UpdateMultipleOverridesResponse,
  VendorReportImageResponse,
  VendorReportsResponse,
  VtLeadVendor
} from '@/types/vendorTransmissionReport';

import { setActiveColorCategory } from '../transmissionReports/transmissionReportsSlice';

export const reportsApiSlice = createApi({
  reducerPath: 'reportApi',
  baseQuery: fetchBaseQuery({
    baseUrl: BASE_URL,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token;
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      return headers;
    }
  }),
  tagTypes: [API_SLICE.TransmissionReport, API_SLICE.Patient],
  endpoints: (builder) => ({
    getPendingReportsCountsByColor: builder.query<
      UnsignedReportCountsByColorResponse,
      UnsignedReportCountsByColorRequest
    >({
      query: (body) => ({
        url: API_ENDPOINTS.TRANSMISSION_REPORTS.FILTERED_COUNT,
        method: 'POST',
        body: body
      }),
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        let cacheKey = `${endpointName}/${queryArgs.signatureStatus}`;
        if ((queryArgs.clinicIds?.length ?? 0) > 0) {
          cacheKey += '/' + (queryArgs.clinicIds ?? []).join(',');
        }
        if ((queryArgs.practitionerIds?.length ?? 0) > 0) {
          cacheKey += '/' + (queryArgs.practitionerIds ?? []).join(',');
        }
        if ((queryArgs.deviceTypes?.length ?? 0) > 0) {
          cacheKey += '/' + (queryArgs.deviceTypes ?? []).join(',');
        }
        if (queryArgs.search !== undefined) {
          cacheKey += `/search=${queryArgs.search}`;
        }
        return cacheKey;
      },
      providesTags: (result) =>
        result
          ? [
              {
                type: API_SLICE.TransmissionReport,
                id: 'COUNTS'
              }
            ]
          : []
    }),
    getTransmissionReports: builder.query<
      TransmissionCategoryResponse,
      TransmissionReportListPaginatedRequestDto
    >({
      query: (body) => ({
        url: API_ENDPOINTS.TRANSMISSION_REPORTS.LIST,
        method: 'POST',
        body: body
      }),
      // Only have one cache entry because the arg always maps to one string
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        let cacheKey = endpointName + '/' + queryArgs.color;
        if ((queryArgs.clinicIds?.length ?? 0) > 0) {
          cacheKey += '/' + (queryArgs.clinicIds ?? []).join(',');
        }
        if ((queryArgs.practitionerIds?.length ?? 0) > 0) {
          cacheKey += '/' + (queryArgs.practitionerIds ?? []).join(',');
        }
        if ((queryArgs.deviceTypes?.length ?? 0) > 0) {
          cacheKey += '/' + (queryArgs.deviceTypes ?? []).join(',');
        }
        if (queryArgs.search !== undefined) {
          cacheKey += `/search=${queryArgs.search}`;
        }
        return cacheKey;
      },
      // Always merge incoming data to the cache entry
      merge: (currentCache, newItems, { arg }) => {
        // If the offset is 0, it means the data is refreshed, so replace the cache
        if (arg.offset === 0) {
          return newItems;
        }
        if (currentCache.data.length) {
          return {
            ...currentCache,
            ...newItems,
            data: [...currentCache.data, ...newItems.data]
          };
        }
        return newItems;
      },
      providesTags: (result, _error, arg) =>
        // is result available?
        result
          ? // successful query
            [
              { type: API_SLICE.TransmissionReport, id: arg.color },
              { type: API_SLICE.TransmissionReport, id: 'LIST' }
            ]
          : // an error occurred, but we still want to refetch this query when `{ type: API_SLICE.TransmissionReport, id: 'LIST' }` is invalidated
            [{ type: API_SLICE.TransmissionReport, id: 'LIST' }]
    }),
    getTransmissionReportDetails: builder.query<TransmissionReportDetailsResponse, number>({
      query: (id) => ({
        url: `${API_ENDPOINTS.TRANSMISSION_REPORTS.ROOT}/${id}`,
        method: 'GET'
      }),
      // temporarily disabling caching for this query
      keepUnusedDataFor: 0,
      providesTags: (_result, _error, id) => [{ type: API_SLICE.TransmissionReport, id }],
      async onQueryStarted(_args, { dispatch, queryFulfilled, getState }) {
        try {
          const rootState = getState() as RootState;
          const { data: txReport } = await queryFulfilled;

          // see if sidebar is enabled
          const isAccordionSidebarOpen = rootState.transmissionReports.isAccordionSidebarOpen;
          if (
            isAccordionSidebarOpen &&
            txReport.transmissionReport.signatureStatus !== 'PENDING_REVIEW'
          ) {
            dispatch(
              setActiveColorCategory({
                color: txReport.transmissionReport.color,
                skipSelectedReportReset: true
              })
            );
          }
        } catch (error) {
          // error catch
          console.log('error while updating tx report store', error);
        }
      }
    }),
    dismissTransmissionReport: builder.mutation<void, number>({
      query: (id) => ({
        url: API_ENDPOINTS.TRANSMISSION_REPORTS.DISMISS(id),
        method: 'PATCH'
      }),
      async onQueryStarted(reportId, { dispatch, queryFulfilled, getState }) {
        try {
          await queryFulfilled;

          const state = getState() as RootState;
          dispatch(
            reportsApiSlice.util.updateQueryData(
              'getTransmissionReports',
              {
                color: state.transmissionReports.activeColorCategory,
                clinicIds: state.preferences.filters?.clinics.map((clinic) => clinic.value),
                practitionerIds: state.preferences.filters?.practitioners.map(
                  (practitioner) => practitioner.value
                ),
                signingPractitionerIds: state.preferences.filters?.signingPractitioners?.map(
                  (signingPractitioner) => signingPractitioner.value
                ),
                deviceTypes: state.preferences.filters?.deviceTypes.map(
                  (deviceType) => deviceType.value
                ),
                vendors: state.preferences.filters?.vendors.map((vendor) => vendor.value),
                limit: 0,
                offset: 0
              },
              (draft) => {
                draft.data = draft.data.filter((report) => report.id !== reportId);
                draft.total -= 1;
                draft.count -= 1;
              }
            )
          );
        } catch (error) {
          // error catch
        }
      },
      invalidatesTags: () => [{ type: API_SLICE.TransmissionReport, id: 'COUNTS' }]
    }),
    dismissMultipleTransmissionReports: builder.mutation<string, number[]>({
      query: (ids) => ({
        url: API_ENDPOINTS.TRANSMISSION_REPORTS.DISMISS_MULTIPLE(ids),
        method: 'POST'
      }),
      invalidatesTags: () => [
        { type: API_SLICE.TransmissionReport, id: 'COUNTS' },
        { type: API_SLICE.TransmissionReport, id: ReportColor.Gray }, // multiple dismiss is always from gray
        { type: API_SLICE.TransmissionReport, id: 'LIST' }
      ]
    }),
    updateReportColor: builder.mutation<
      TransmissionReportDetailsResponse,
      { id: number } & { currentColor?: string } & Partial<UpdateTransmissionReportParams>
    >({
      query: ({ id, ...data }) => ({
        url: `${API_ENDPOINTS.TRANSMISSION_REPORTS.ROOT}/${id}`,
        method: 'PATCH',
        body: data
      }),
      invalidatesTags: (_result, _error, { id, currentColor }) => [
        { type: API_SLICE.TransmissionReport, id: currentColor },
        { type: API_SLICE.TransmissionReport, id }
      ]
    }),
    patchTransmissionReport: builder.mutation<
      TransmissionReportDetailsResponse,
      { id: number } & Partial<UpdateTransmissionReportParams>
    >({
      query: ({ id, ...data }) => ({
        url: `${API_ENDPOINTS.TRANSMISSION_REPORTS.ROOT}/${id}`,
        method: 'PATCH',
        body: data
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: API_SLICE.TransmissionReport, id }],
      async onQueryStarted({ id, monitorReason }, { dispatch, queryFulfilled }) {
        if (monitorReason === undefined) return;
        const patchResult = dispatch(
          reportsApiSlice.util.updateQueryData('getTransmissionReportDetails', id, (draft) => {
            if (draft?.transmissionReport) {
              draft.transmissionReport.monitorReason = monitorReason;
            }
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      }
    }),
    patchReportInClinicEpisode: builder.mutation<
      { message: string; success: boolean },
      { reportId: number } & Partial<ReportInClinicEpisode>
    >({
      query: ({ reportId, ...data }) => ({
        url: API_ENDPOINTS.TRANSMISSION_REPORTS.INCLINIC_EPISODE(reportId),
        method: 'PATCH',
        body: data
      })
    }),
    getVtVendorsList: builder.query<Array<VtLeadVendor>, void>({
      query: () => ({
        url: API_ENDPOINTS.TRANSMISSION_REPORTS.LEAD_VENDOR_LIST,
        method: 'GET'
      }),
      // save data for 24 hours
      keepUnusedDataFor: UNUSED_DATA_RETENTION_SECONDS
    }),
    createAlert: builder.mutation<Alert, CreateAlertRequest>({
      query: ({ params, reportId }) => ({
        url: API_ENDPOINTS.ALERTS.CREATE_ALERT(reportId),
        method: 'POST',
        body: params
      }),
      async onQueryStarted({ ...args }, { dispatch, queryFulfilled }) {
        try {
          const { data: newAlert } = await queryFulfilled;
          dispatch(
            reportsApiSlice.util.updateQueryData(
              'getTransmissionReportDetails',
              args.reportId,
              (draft) => {
                draft?.transmissionReport.alerts.push(newAlert);
              }
            )
          );
        } catch (error) {
          // invalidate the query to refetch the data
          dispatch(
            reportsApiSlice.util.invalidateTags([
              { type: API_SLICE.TransmissionReport, id: args.reportId }
            ])
          );
        }
      }
    }),
    updateAlertTitle: builder.mutation<AlertResponse, UpdateAlertRequest>({
      query: ({ params, reportId, alertId }) => ({
        url: API_ENDPOINTS.ALERTS.UPDATE_ALERT(reportId, alertId),
        method: 'PATCH',
        body: params
      }),
      async onQueryStarted({ ...args }, { dispatch, queryFulfilled }) {
        try {
          const { data: newAlert } = await queryFulfilled;
          dispatch(
            reportsApiSlice.util.updateQueryData(
              'getTransmissionReportDetails',
              args.reportId,
              (draft) => {
                const alertIndex = draft?.transmissionReport.alerts.findIndex(
                  (alert) => alert.id === newAlert.alert.id
                );
                if (alertIndex !== -1 && draft) {
                  draft.transmissionReport.alerts[alertIndex].title = newAlert.alert.title;
                } else throw new Error('Alert not found');
              }
            )
          );
        } catch (error) {
          // invalidate the query to refetch the data
          dispatch(
            reportsApiSlice.util.invalidateTags([
              { type: API_SLICE.TransmissionReport, id: args.reportId }
            ])
          );
        }
      }
    }),
    deleteAlert: builder.mutation<AlertResponse, DeleteAlertRequest>({
      query: ({ reportId, alertId }) => ({
        url: API_ENDPOINTS.ALERTS.DELETE_ALERT(reportId, alertId),
        method: 'DELETE'
      }),
      async onQueryStarted({ ...args }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            reportsApiSlice.util.updateQueryData(
              'getTransmissionReportDetails',
              args.reportId,
              (draft) => {
                const alertIndex = draft?.transmissionReport.alerts.findIndex(
                  (alert) => alert.id === args.alertId
                );
                if (alertIndex !== -1 && draft) {
                  draft.transmissionReport.alerts.splice(alertIndex, 1);
                } else throw new Error('Alert not found');
              }
            )
          );
        } catch (error) {
          // invalidate the query to refetch the data
          dispatch(
            reportsApiSlice.util.invalidateTags([
              { type: API_SLICE.TransmissionReport, id: args.reportId }
            ])
          );
        }
      }
    }),
    addImageToAlert: builder.mutation<ReportImage, AddImageToAlertRequest>({
      query: ({ transmissionReportId, alertId, transmissionReportAddAlertImageParams }) => ({
        url: API_ENDPOINTS.ALERTS.UPLOAD_IMAGE(transmissionReportId, alertId),
        method: 'POST',
        body: transmissionReportAddAlertImageParams
      }),
      async onQueryStarted({ ...args }, { dispatch, queryFulfilled }) {
        try {
          const { data: newImage } = await queryFulfilled;
          dispatch(
            reportsApiSlice.util.updateQueryData(
              'getTransmissionReportDetails',
              args.transmissionReportId,
              (draft) => {
                const alertIndex = draft?.transmissionReport.alerts.findIndex(
                  (alert) => alert.id === args.alertId
                );
                if (alertIndex !== -1 && draft) {
                  draft.transmissionReport.alerts[alertIndex].images.push(newImage.image);
                } else throw new Error('Alert not found');
              }
            )
          );
        } catch (error) {
          // invalidate the query to refetch the data
          dispatch(
            reportsApiSlice.util.invalidateTags([
              {
                type: API_SLICE.TransmissionReport,
                id: args.transmissionReportId
              }
            ])
          );
        }
      }
    }),
    deleteImageFromAlert: builder.mutation<void, RemoveImageFromAlertRequest>({
      query: ({ transmissionReportId, alertId, imageId }) => ({
        url: API_ENDPOINTS.ALERTS.DELETE_IMAGE(transmissionReportId, alertId, imageId),
        method: 'DELETE'
      }),
      async onQueryStarted({ ...args }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            reportsApiSlice.util.updateQueryData(
              'getTransmissionReportDetails',
              args.transmissionReportId,
              (draft) => {
                const alertIndex = draft?.transmissionReport.alerts.findIndex(
                  (alert) => alert.id === args.alertId
                );
                if (alertIndex !== -1 && draft) {
                  const imageIndex = draft.transmissionReport.alerts[alertIndex].images.findIndex(
                    (image) => image.id === args.imageId
                  );
                  if (imageIndex !== -1) {
                    draft.transmissionReport.alerts[alertIndex].images.splice(imageIndex, 1);
                  }
                } else throw new Error('Alert not found');
              }
            )
          );
        } catch (error) {
          // invalidate the query to refetch the data
          dispatch(
            reportsApiSlice.util.invalidateTags([
              {
                type: API_SLICE.TransmissionReport,
                id: args.transmissionReportId
              }
            ])
          );
        }
      }
    }),
    getVendorReportImage: builder.query<VendorReportImageResponse, number>({
      query: (id) => ({
        url: API_ENDPOINTS.TRANSMISSION_REPORTS.VENDOR_REPORT_IMAGE(id),
        method: 'GET'
      })
    }),
    getVendorReports: builder.query<VendorReportsResponse, number>({
      query: (id) => ({
        url: API_ENDPOINTS.TRANSMISSION_REPORTS.VENDOR_REPORTS(id),
        method: 'GET'
      }),
      // Disabling caching for this query because the Lambda function updates the report
      // when users edit a saved report. Caching prevents fetching the latest data,
      // forcing users to refresh manually.
      keepUnusedDataFor: 1,
      providesTags: (_result, _error, id) => [
        { type: API_SLICE.TransmissionReport, id: `vendor_reports_${id}` }
      ]
    }),
    getReportAttachments: builder.query<GetReportAttachmentsResponse, number>({
      query: (id) => ({
        url: API_ENDPOINTS.TRANSMISSION_REPORTS.ATTACHMENTS(id),
        method: 'GET'
      }),
      providesTags: (_result, _error, id) => [
        { type: API_SLICE.TransmissionReport, id: `attachment_${id}` }
      ]
    }),
    addReportAttachments: builder.mutation<void, AddAttachmentsRequest>({
      query: ({ reportId, files, deletedFiles }) => {
        const formData = new FormData();
        files.forEach((file) => formData.append('files[]', file));
        deletedFiles.forEach((file) => formData.append('deletedFiles', file));
        return {
          url: API_ENDPOINTS.TRANSMISSION_REPORTS.ATTACHMENTS(reportId),
          method: 'POST',
          body: formData
        };
      },
      invalidatesTags: (_result, _error, { reportId }) => [
        { type: API_SLICE.TransmissionReport, id: `attachment_${reportId}` },
        { type: API_SLICE.TransmissionReport, id: `vendor_reports_${reportId}` }
      ]
    }),
    addCounters: builder.mutation<CountersResponse, CreateCountersRequest>({
      query: ({ transmissionReportId, transmissionReportCountersCreateParams }) => ({
        url: API_ENDPOINTS.COUNTERS.CREATE(transmissionReportId),
        method: 'POST',
        body: transmissionReportCountersCreateParams
      }),
      async onQueryStarted({ ...args }, { dispatch, queryFulfilled }) {
        try {
          const res = await queryFulfilled;
          dispatch(
            reportsApiSlice.util.updateQueryData(
              'getTransmissionReportDetails',
              args.transmissionReportId,
              (draft) => {
                draft.transmissionReport.counters = {
                  ...draft.transmissionReport.counters,
                  ...args.transmissionReportCountersCreateParams.countersobject,
                  Id: res.data.counters.Id,
                  updatedAt: res.data.counters.updatedAt
                };
              }
            )
          );
        } catch (error) {
          // invalidate the query to refetch the data
          dispatch(
            reportsApiSlice.util.invalidateTags([
              {
                type: API_SLICE.TransmissionReport,
                id: args.transmissionReportId
              }
            ])
          );
        }
      }
    }),
    updateCounters: builder.mutation<CountersResponse, UpdateCountersRequest>({
      query: ({ transmissionReportId, transmissionReportCountersUpdateParams }) => ({
        url: API_ENDPOINTS.COUNTERS.UPDATE(transmissionReportId),
        method: 'PATCH',
        body: transmissionReportCountersUpdateParams
      }),
      async onQueryStarted({ ...args }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            reportsApiSlice.util.updateQueryData(
              'getTransmissionReportDetails',
              args.transmissionReportId,
              (draft) => {
                // Same reasoning as addCounters — write the request payload rather
                // than the server response to avoid counter values disappearing.
                draft.transmissionReport.counters = {
                  ...draft.transmissionReport.counters,
                  ...args.transmissionReportCountersUpdateParams.countersobject
                };
              }
            )
          );
        } catch (error) {
          dispatch(
            reportsApiSlice.util.invalidateTags([
              {
                type: API_SLICE.TransmissionReport,
                id: args.transmissionReportId
              }
            ])
          );
        }
      }
    }),
    updateProgrammedParameters: builder.mutation<
      ProgrammedParameters,
      UpdateProgrammedParametersRequest
    >({
      query: ({ transmissionReportId, programmedParametersUpdateParams }) => ({
        url: API_ENDPOINTS.PROGRAMMED_PARAMETERS.UPDATE(transmissionReportId),
        method: 'PATCH',
        body: programmedParametersUpdateParams
      })
    }),
    updateMultipleNotes: builder.mutation<UpdateMultipleNotesResponse, UpdateMultipleNotesRequest>({
      query: ({ transmissionReportId, notes }) => ({
        url: API_ENDPOINTS.NOTES.UPDATE_MULTIPLE(transmissionReportId),
        method: 'POST',
        body: { notes }
      })
    }),
    updateMultipleOverrides: builder.mutation<
      UpdateMultipleOverridesResponse,
      UpdateMultipleOverridesRequest
    >({
      query: ({ transmissionReportId, overrides }) => ({
        url: API_ENDPOINTS.TRANSMISSION_REPORTS.UPDATE_MULTIPLE_OVERRIDES(transmissionReportId),
        method: 'PATCH',
        body: overrides
      })
    }),
    signReport: builder.mutation<SignReportResponse, SignReportRequest>({
      query: ({ reportId, signDateParams }) => ({
        url: API_ENDPOINTS.TRANSMISSION_REPORTS.SIGN(reportId),
        method: 'PATCH',
        body: signDateParams
      }),
      invalidatesTags: (_result, _error, { reportId }) => [
        { type: API_SLICE.TransmissionReport, id: reportId }
      ]
    }),
    getReportTemplates: builder.query<FetchReportTemplatesResponse, FetchReportTemplatesRequest>({
      query: ({ reportId, deviceType, vendor }) => ({
        url: API_ENDPOINTS.REPORT_TEMPLATE.LIST(reportId),
        method: 'GET',
        params: { deviceType, vendor }
      })
    }),
    applyReportTemplate: builder.mutation<ReportTemplateResponse, ApplyReportTemplateRequest>({
      query: ({ reportId, templateId }) => ({
        url: API_ENDPOINTS.REPORT_TEMPLATE.APPLY,
        method: 'POST',
        body: { reportId, templateId }
      }),
      invalidatesTags: (_result, _error, { reportId }) => [
        { type: API_SLICE.TransmissionReport, id: reportId }
      ],
      async onQueryStarted({ ...args }, { dispatch, queryFulfilled, getState }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            billingApiSlice.util.invalidateTags([{ type: API_SLICE.Billing, id: args.reportId }])
          );
          if (!data.template?.IS_HEART_FAILURE) return;

          const state = getState() as RootState;
          dispatch(
            reportsApiSlice.util.updateQueryData(
              'getTransmissionReports',
              {
                color: state.transmissionReports.activeColorCategory,
                clinicIds: state.preferences.filters?.clinics.map((clinic) => clinic.value),
                practitionerIds: state.preferences.filters?.practitioners.map(
                  (practitioner) => practitioner.value
                ),
                signingPractitionerIds: state.preferences.filters?.signingPractitioners?.map(
                  (signingPractitioner) => signingPractitioner.value
                ),
                deviceTypes: state.preferences.filters?.deviceTypes.map(
                  (deviceType) => deviceType.value
                ),
                vendors: state.preferences.filters?.vendors.map((vendor) => vendor.value),
                limit: 0,
                offset: 0
              },
              (draft) => {
                draft.data.forEach((report) => {
                  if (report.id !== args.reportId) return;
                  report.reportType = 'HEART_FAILURE';
                });
              }
            )
          );
        } catch (error) {
          // error catch
        }
      }
    }),
    removeReportTemplate: builder.mutation<ReportTemplateResponse, number>({
      query: (reportId) => ({
        url: API_ENDPOINTS.REPORT_TEMPLATE.REMOVE,
        method: 'POST',
        body: { reportId }
      }),
      invalidatesTags: (_result, _error, reportId) => [
        { type: API_SLICE.TransmissionReport, id: reportId }
      ],
      async onQueryStarted(reportId, { dispatch, queryFulfilled, getState }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            billingApiSlice.util.invalidateTags([{ type: API_SLICE.Billing, id: reportId }])
          );
          if (!data.template?.IS_HEART_FAILURE) return;

          const state = getState() as RootState;
          dispatch(
            reportsApiSlice.util.updateQueryData(
              'getTransmissionReports',
              {
                color: state.transmissionReports.activeColorCategory,
                clinicIds: state.preferences.filters?.clinics.map((clinic) => clinic.value),
                practitionerIds: state.preferences.filters?.practitioners.map(
                  (practitioner) => practitioner.value
                ),
                signingPractitionerIds: state.preferences.filters?.signingPractitioners?.map(
                  (signingPractitioner) => signingPractitioner.value
                ),
                deviceTypes: state.preferences.filters?.deviceTypes.map(
                  (deviceType) => deviceType.value
                ),
                vendors: state.preferences.filters?.vendors.map((vendor) => vendor.value),
                limit: 0,
                offset: 0
              },
              (draft) => {
                draft.data.forEach((report) => {
                  if (report.id !== reportId) return;
                  report.reportType = 'ROUTINE';
                });
              }
            )
          );
        } catch (error) {
          // error catch
        }
      }
    }),
    replaceReportTemplate: builder.mutation<
      ReplaceReportTemplateResponse,
      ReplaceReportTemplateRequest
    >({
      query: ({ reportId, templateId }) => ({
        url: API_ENDPOINTS.REPORT_TEMPLATE.REPLACE,
        method: 'POST',
        body: { reportId, templateId }
      }),
      invalidatesTags: (_result, _error, { reportId }) => [
        { type: API_SLICE.TransmissionReport, id: reportId }
      ],
      async onQueryStarted({ ...args }, { dispatch, queryFulfilled, getState }) {
        try {
          const { data } = await queryFulfilled;

          dispatch(
            billingApiSlice.util.invalidateTags([{ type: API_SLICE.Billing, id: args.reportId }])
          );
          if (!data.applied?.IS_HEART_FAILURE && !data.removed?.IS_HEART_FAILURE) return;
          const newReportType =
            data.applied?.IS_HEART_FAILURE === true ? 'HEART_FAILURE' : 'ROUTINE';

          const state = getState() as RootState;
          dispatch(
            reportsApiSlice.util.updateQueryData(
              'getTransmissionReports',
              {
                color: state.transmissionReports.activeColorCategory,
                clinicIds: state.preferences.filters?.clinics.map((clinic) => clinic.value),
                practitionerIds: state.preferences.filters?.practitioners.map(
                  (practitioner) => practitioner.value
                ),
                signingPractitionerIds: state.preferences.filters?.signingPractitioners?.map(
                  (signingPractitioner) => signingPractitioner.value
                ),
                deviceTypes: state.preferences.filters?.deviceTypes.map(
                  (deviceType) => deviceType.value
                ),
                vendors: state.preferences.filters?.vendors.map((vendor) => vendor.value),
                limit: 0,
                offset: 0
              },
              (draft) => {
                draft.data.forEach((report) => {
                  if (report.id !== args.reportId) return;
                  report.reportType = newReportType;
                });
              }
            )
          );
        } catch (error) {
          // error catch
        }
      }
    }),
    searchUnsignedReports: builder.query<
      SearchUnsignedReportsResponse,
      SearchUnsignedReportsRequest
    >({
      query: ({ color, query, excludeGlobalFilters, clinicIds, practitioners, deviceTypes }) => ({
        url: API_ENDPOINTS.TRANSMISSION_REPORTS.SEARCH_UNSIGNED_REPORTS,
        method: 'POST',
        body: {
          color,
          query,
          excludeGlobalFilters: excludeGlobalFilters ?? true,
          clinicIds: clinicIds ?? [],
          practitioners: practitioners ?? [],
          deviceTypes: deviceTypes ?? []
        }
      })
    }),
    storePdfDownloadInfo: builder.mutation<void, { txId: number; downloadDate: string }>({
      query: ({ txId, downloadDate }) => ({
        url: API_ENDPOINTS.TRANSMISSION_REPORTS.STORE_PDF_DOWNLOAD_INFO(txId, downloadDate),
        method: 'PATCH'
      }),
      async onQueryStarted(_params, { dispatch, queryFulfilled, getState }) {
        try {
          await queryFulfilled;

          const state = getState() as RootState;
          const { filters: localFilters, pageLimit, skip } = state.patients;

          const { filters: globalFilters } = state.preferences;

          dispatch(
            patientsApiSlice.endpoints.getAllPatients.initiate(
              {
                skip,
                pageLimit: pageLimit ?? 10,
                forV2: true,
                ...getFilters(localFilters, globalFilters)
              },
              { forceRefetch: true }
            )
          );
        } catch (error) {
          console.error('Mutation failed:', error);
        }
      }
    }),
    unDismissReport: builder.mutation<void, number>({
      query: (reportId) => ({
        url: API_ENDPOINTS.TRANSMISSION_REPORTS.UNDISMISS(reportId),
        method: 'PATCH'
      }),
      invalidatesTags: (_result, _error, reportId) => [
        { type: API_SLICE.TransmissionReport, id: reportId }
      ]
    }),
    storeTempInClinicPdf: builder.mutation<
      { preSignedUrl: string; id: string },
      { patientId: string; fileName: string }
    >({
      query: ({ patientId, fileName }) => ({
        url: API_ENDPOINTS.TRANSMISSION_REPORTS.UPLOAD_IN_CLINIC_TEMP_PDF(patientId),
        body: { fileName },
        method: 'POST'
      })
    }),
    saveManualInClinicReports: builder.mutation<
      { fileStatus: Array<{ fileName: string; status: string }> },
      { reportType: string; patientId: string; deviceId: string; files: string[] }
    >({
      query: (body) => ({
        url: API_ENDPOINTS.TRANSMISSION_REPORTS.SAVE_MANUAL_IN_CLINIC_REPORT,
        method: 'POST',
        body
      }),
      invalidatesTags: (_result, _error, { patientId }) => [
        { type: API_SLICE.Patient, id: patientId },
        {
          type: API_SLICE.TransmissionReport,
          id: 'COUNTS'
        }
      ],
      async onQueryStarted({ patientId }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;

          // Manually trigger refetch of getPatient query
          dispatch(
            patientsApiSlice.endpoints.getPatient.initiate(patientId, { forceRefetch: true })
          );
        } catch (error) {
          console.error('Mutation failed:', error);
        }
      }
    }),
    editSignedReport: builder.mutation<void, { reportId: number; isEditable: boolean; file: File }>(
      {
        query: ({ reportId, isEditable, file }) => {
          const formData = new FormData();
          formData.append('file', file);
          return {
            url: API_ENDPOINTS.TRANSMISSION_REPORTS.EDIT_SIGNED_REPORT(reportId, isEditable),
            method: 'POST',
            body: formData
          };
        }
      }
    ),
    getReportAttachmentUploadUrl: builder.mutation<
      { preSignedUrl: string; s3Key: string; filename: string },
      { reportId: number; filename: string }
    >({
      query: ({ reportId, filename }) => ({
        url: `${API_ENDPOINTS.TRANSMISSION_REPORTS.ATTACHMENTS(reportId)}/upload-url`,
        method: 'POST',
        body: { filename }
      })
    }),
    saveReportAttachments: builder.mutation<
      void,
      {
        reportId: number;
        attachments: Array<{ filename: string; originalFileName: string; s3Key: string }>;
        deletedFiles: string[];
      }
    >({
      query: ({ reportId, attachments, deletedFiles }) => ({
        url: `${API_ENDPOINTS.TRANSMISSION_REPORTS.ATTACHMENTS(reportId)}/save`,
        method: 'POST',
        body: { attachments, deletedFiles }
      }),
      invalidatesTags: (_result, _error, { reportId }) => [
        { type: API_SLICE.TransmissionReport, id: `attachment_${reportId}` },
        { type: API_SLICE.TransmissionReport, id: `vendor_reports_${reportId}` }
      ]
    }),
    getNavigationReportIds: builder.query<
      {
        previousTransmissionReportId: string;
        nextTransmissionReportId: string;
      },
      NavigationReportIdsRequestDto
    >({
      query: (params) => ({
        url: API_ENDPOINTS.TRANSMISSION_REPORTS.NAVIGATION(params.reportId),
        method: 'POST',
        body: params
      }),
      keepUnusedDataFor: 2,
      providesTags: (_result, _error, { reportId }) => [
        { type: API_SLICE.TransmissionReport, id: `navigation_${reportId}` }
      ]
    }),
    getPatientReports: builder.query<GetPatientReportsResponse, { patientId: string }>({
      query: ({ patientId }) => ({
        url: API_ENDPOINTS.TRANSMISSION_REPORTS.GET_PATIENT_REPORTS(patientId),
        method: 'GET'
      })
    }),
    getOrganizationReportTemplates: builder.query<
      FetchOrgPhysicianTemplatesResponse,
      { organizationId: string }
    >({
      query: ({ organizationId }) => ({
        url: API_ENDPOINTS.REPORT_TEMPLATE.ORGANIZATION_LIST(organizationId),
        method: 'GET'
      })
    })
  })
});

export const {
  useGetPendingReportsCountsByColorQuery,
  useGetTransmissionReportsQuery,
  useGetTransmissionReportDetailsQuery,
  useDismissTransmissionReportMutation,
  useDismissMultipleTransmissionReportsMutation,
  useUpdateReportColorMutation,
  usePatchTransmissionReportMutation,
  useGetVtVendorsListQuery,
  useCreateAlertMutation,
  useUpdateAlertTitleMutation,
  useDeleteAlertMutation,
  useAddImageToAlertMutation,
  useDeleteImageFromAlertMutation,
  useGetVendorReportImageQuery,
  useGetVendorReportsQuery,
  useLazyGetVendorReportsQuery,
  useGetReportAttachmentsQuery,
  useAddReportAttachmentsMutation,
  useAddCountersMutation,
  useUpdateCountersMutation,
  useUpdateProgrammedParametersMutation,
  useUpdateMultipleNotesMutation,
  useUpdateMultipleOverridesMutation,
  useSignReportMutation,
  useGetReportTemplatesQuery,
  useApplyReportTemplateMutation,
  useRemoveReportTemplateMutation,
  useReplaceReportTemplateMutation,
  useSearchUnsignedReportsQuery,
  useLazySearchUnsignedReportsQuery,
  useStorePdfDownloadInfoMutation,
  usePatchReportInClinicEpisodeMutation,
  useUnDismissReportMutation,
  useStoreTempInClinicPdfMutation,
  useSaveManualInClinicReportsMutation,
  useEditSignedReportMutation,
  useGetNavigationReportIdsQuery,
  useLazyGetNavigationReportIdsQuery,
  useGetPatientReportsQuery,
  useGetReportAttachmentUploadUrlMutation,
  useSaveReportAttachmentsMutation,
  useGetOrganizationReportTemplatesQuery
} = reportsApiSlice;
